import Vue from 'vue'
import brandTagDisplay from 'mixins/brand-tag'
import { i18n } from 'lib/i18n.js'
import { mapActions } from 'vuex'
import {
  decode,
  slug,
  formatPrice,
  generateSrcset
} from 'utils/general'

/**
 * This component is used throughout the site,
 * in product carousels and on the PLP page.
 */
Vue.component('quick-view', resolve => {
  Promise.all([]).then(() => resolve({
    mixins: [brandTagDisplay],
    i18n,
    props: ['productJson', 'activeVariant'],
    data () {
      return {
        available: true,
        product: '',
        quantity: 1,
        activeProductVariant: this.activeVariant,
        activeColor: '',
        options: [
          false,
          false,
          false
        ],
        swatchImages: '',
        optionMap: {}
      }
    },
    mounted () {
      this.product = JSON.parse(decode(this.productJson))
      this.initialOptions()
      this.buildOptionMap()

      const swatchThumbnails = this.product.images.filter(image => {
        let altText = ''

        if (image.alt) {
          altText = image.alt.toLowerCase()
        }

        if (altText.includes('swatch_')) {
          return image
        }
      })

      if (swatchThumbnails.length > 0) {
        this.swatchImages = swatchThumbnails.map(swatchThumbnail => ({
          handle: swatchThumbnail.alt.toLowerCase().split('swatch_')[1].split('|')[0].trim().replaceAll(' ', '-'),
          src: swatchThumbnail.src
        }))
      }

      if (this.colorOptionIndex > -1) {
        this.setColorOption(this.options[this.colorOptionIndex])
      }
    },
    updated: function () {
      this.$nextTick(function () {
        if (typeof _swat !== 'undefined') {
          _swat.initializeActionButtons('.collection')
          _swat.initializeActionButtons('.post__product')
        }
      })
    },
    computed: {
      promoTag () {
        let promoTag = this.product.tags.find(tag => slug(tag).includes('promo:'))

        if (promoTag) {
          promoTag = promoTag.replace('[comma]', ',').split(':')[1].trim()
        }

        return promoTag
      },
      getProduct () {
        return this.product
      },
      productImage () {
        let sku = false

        if (this.activeProductVariant.sku) {
          sku = slug(this.activeProductVariant.sku)
        }

        let featuredImage = this.product.featuredImage
        const skuBasedImage = this.product.images.find(img => {
          if (img.alt) {
            if (img.alt.includes('|')) {
              const altText = slug(img.alt).split('|')
              return ~altText.indexOf(sku)
            }
          }
        })

        if (skuBasedImage) {
          featuredImage = skuBasedImage.src
        }

        return featuredImage
      },
      link () {
        let collection = ''
        const pathname = window.location.pathname

        if (pathname.includes('collections')) {
          collection = pathname
        }

        return `${collection}/products/${this.product.handle}?variant=${this.activeProductVariant.id}`
      },
      colorOptionIndex () {
        return this.product.optionNames.indexOf('Color')
      },
      title () {
        return this.product.title.replaceAll('[escaped]', '"')
      },
      activeVariantPrice () {
        if (!this.activeProductVariant) {
          return false
        }
        if (this.activeProductVariant.compareAtPrice !== 'NaN' && this.activeProductVariant.compareAtPrice) {
          return formatPrice(this.activeProductVariant.compareAtPrice)
        }
        return formatPrice(this.activeProductVariant.price)
      },
      activeVariantDiscountPrice () {
        if (!this.activeProductVariant || this.activeProductVariant.compareAtPrice === 'NaN' || !this.activeProductVariant.compareAtPrice) {
          return false
        }
        return formatPrice(this.activeProductVariant.price)
      },
      activeVariantDisabled () {
        this.available = [false, 'false'].includes(this.activeProductVariant.available)
        return this.available
      },
      buttonText () {
        if (this.activeVariantDisabled) {
          return this.$t('products.product.sold_out')
        } else {
          return this.$t('products.product.add_to_cart')
        }
      }
    },
    methods: {
      ...mapActions('cart', [
        'addToCart'
      ]),
      ...mapActions('product', [
        'setActiveVariant',
        'updateProduct'
      ]),
      ...mapActions('product', [
        'setNewOptionsMap'
      ]),
      ...mapActions('global', [
        'openModal'
      ]),
      openNotifyModal () {
        this.updateProduct(this.product)
        this.setNewOptionsMap(this.optionMap)
        this.openModal('stockNotificationForm')
      },
      generateSrcset (url) {
        return generateSrcset(url)
      },
      addProduct () {
        const properties = {}
        // add maxquantity limit to prevent adding more items to cart
        properties._maxquantity = this.activeProductVariant.inventoryQuantity

        // check if active variant has discounted price
        if (this.activeProductVariant.compareAtPrice !== 'NaN') {
          properties._discounted = this.activeProductVariant.compareAtPrice
        }

        this.addToCart({
          id: this.activeProductVariant.id,
          quantity: this.quantity,
          properties,
          withProperties: true
        })
      },
      updateQuantity (quantity) {
        this.quantity = quantity
      },
      onSelectChange ({ value, name }) {
        const index = this.product.optionNames.indexOf(name)
        this.options[index] = value

        this.updateVariant()
      },
      valueChange () {
        this.validItemsCount = this.$children
          .filter(c => (
            typeof c.validateInput === 'undefined' ||
            c.isValid
          )).length
      },
      buildOptionMap () {
        Object.keys(this.product.optionsWithValues).forEach((key, i) => {
          this.optionMap[key] = this.product.optionsWithValues[key].map(value => {
            return {
              label: value.replaceAll('[escaped]', '"'),
              value: value,
              enabled: true
            }
          })
        })
      },
      selectInitialValue (values) {
        let initialValue = values[0]

        const option1 = this.activeProductVariant.option1
        const option2 = this.activeProductVariant.option2
        const option3 = this.activeProductVariant.option3

        values.forEach(val => {
          if (val === option1 || val === option2 || val === option3) {
            initialValue = val
          }
        })

        return initialValue
      },
      initialOptions () {
        this.options = [this.activeProductVariant.option1, this.activeProductVariant.option2, this.activeProductVariant.option3]
      },
      swatchImage (value) {
        const slugValue = slug(value)
        let backgroundImage = ''

        if (this.swatchImages) {
          const imageSrc = this.swatchImages.find(x => x.handle === slugValue)

          if (imageSrc) {
            backgroundImage = `background-image: url(${imageSrc.src.replace(/([.](?:jpe?g|png))/, '_80x$1')})`
          }
        }

        return backgroundImage
      },
      updateVariant () {
        const filterEmptyValues = this.options.filter(x => x !== 'false')
        const variantTitle = filterEmptyValues.join(' / ')
        const variant = this.product.variants.find(variant => {
          return variant.title === variantTitle
        })

        if (variant) {
          this.activeProductVariant = variant
          this.setActiveVariant(variant)
        }
      },
      setColorOption (swatch) {
        this.options[this.colorOptionIndex] = swatch
        this.activeColor = swatch

        this.updateVariant()
      }
    }
  }))
})
